import moment from "moment"
import Image from "next/image"
import Link from "next/link"
import Script from "next/script"

import RichTextResponse from "@/helpers/renderRichText"
import { categoriesToExclude } from "@/helpers/utils"
import { TAuthor } from "@/types/author"
import LatestBlogPostsCarousel from "@/ui/components/LatestBlogPostsCarousel"
import type { TLatestBlogPosts } from "@/ui/components/LatestBlogPostsCarousel"
import PageGrid from "@/ui/components/PageGrid"
import Pills from "@/ui/components/Pills"
import PostMeta from "@/ui/components/PostMeta"
import SocialShare from "@/ui/components/SocialShare"

import type { IndividualBlogPostProps } from "../../_types/insights"
import NewsletterSubscribeForm from "@/ui/components/NewsletterSubscribeForm"

export default function IndividualBlogPost({
  blogPost,
  latestBlogs,
  authors,
}: {
  blogPost: IndividualBlogPostProps
  latestBlogs: TLatestBlogPosts
  authors: TAuthor[]
}) {
  const {
    title,
    slug,
    contentRich,
    seo,
    sys,
    date: dateUnformatted,
    categoriesCollection,
  } = blogPost
  const { id: databaseId } = sys
  const { featuredImage, seoDescription, seoTitle } = seo
  const date = moment(dateUnformatted).format("MMMM D, YYYY")

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": blogPost.title,
    "image": [
      seo.featuredImage?.url || "https://ripple.com/images/default.png",
    ],
    "datePublished": blogPost.date,
    "author": authors?.length
      ? authors?.map((person) => ({
          "@type": "Person",
          "name":
            person?.firstName && person?.lastName
              ? `${person?.firstName} ${person?.lastName}`
              : "Team Ripple",
        }))
      : [
          {
            "@type": "Person",
            "name": "Team Ripple",
          },
        ],
  }

  const socialShareProps = {
    description: seoDescription || seoTitle || title || "",
    title: seoTitle || title || "Ripple Insights",
    uri: slug ? `/${slug}` : "https://ripple.com/insights/",
  }

  const categoryTags = categoriesCollection?.items?.filter(
    (item) => !categoriesToExclude.includes(item.slug),
  )

  const fallbackImageWidth = 1152
  const fallbackImageHeight = 648
  const minWidthSize = featuredImage?.width
    ? Math.min(featuredImage?.width, 1200)
    : 1200

  return (
    <div className="text-gray-2.5 bg-gray-90">
      <Script src="/post.js" />
      <PageGrid className="pt-8">
        <Link href="/insights">
          <h5 className="p-4 heading1">Insights</h5>
        </Link>
      </PageGrid>

      <article
        className={`post-${databaseId} post single-post type-post status-publish format-standard has-post-thumbnail hentry category-uncategorized`}
        id={`post-${databaseId}`}
        itemScope
        itemType="http://schema.org/Article"
      >
        <Script
          id="schema-org-article"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
        <PageGrid>
          <div className="pl-4 pr-4 col-span-full lg:col-span-8 lg:col-start-3">
            <PostMeta title={title} date={date} authors={authors} />
            {/* tags */}
            {categoryTags && categoryTags.length > 0 && (
              <div className="flex flex-wrap pb-4 mt-8">
                {categoryTags?.map(
                  (cat, index) =>
                    cat?.slug !== "featured" && (
                      <Pills
                        className="inline-flex mr-4 text-gray-30 bg-gray-80"
                        key={cat?.name || index}
                      >
                        {cat?.name}
                      </Pills>
                    ),
                )}
              </div>
            )}
            {/* Share icons */}
            <SocialShare {...socialShareProps} />
          </div>
          {/* Featured Image */}
          <div className="flex justify-center pt-8 pb-16 lg:pt-16 col-span-full">
            <div
              className="w-full overflow-hidden rounded-md"
              style={{
                maxWidth: `${featuredImage?.width || fallbackImageWidth}px`,
              }}
            >
              <div
                className="w-full rounded-lg relative max-h-140"
                style={{
                  aspectRatio: `${featuredImage?.width || fallbackImageWidth}/${featuredImage?.height || fallbackImageHeight}`,
                }}
              >
                {featuredImage?.url ? (
                  <Image
                    src={featuredImage?.url}
                    alt={
                      featuredImage?.title ||
                      featuredImage?.description ||
                      title ||
                      "Ripple Feature Image"
                    }
                    fill
                    sizes={`(min-width: ${minWidthSize}px) ${minWidthSize}px, 100vw`}
                    className="m-0 p-0 object-cover"
                    itemProp="image"
                  />
                ) : (
                  <Image
                    src="/images/default.png"
                    alt="Ripple Image"
                    fill
                    sizes={`(min-width: ${fallbackImageWidth}px) ${fallbackImageWidth}px, 100vw`}
                    className="m-0 p-0 object-cover"
                  />
                )}
              </div>
            </div>
          </div>
        </PageGrid>

        {/* Main content area */}
        <div className="pb-14 lg:pb-16 md:pt-16 pt-12 lg:pt-20 bg-gray-2.5">
          <PageGrid>
            <div className="relative col-span-full lg:col-span-8 lg:col-start-3 post-inner">
              <div className="px-4 entry-content" itemProp="articleBody">
                <RichTextResponse
                  legal={false}
                  richTextResponse={contentRich}
                />
              </div>
            </div>
          </PageGrid>
        </div>
      </article>
      <NewsletterSubscribeForm />
      <LatestBlogPostsCarousel data={latestBlogs} title="Related stories" />
    </div>
  )
}
